// Restores the webview's viewport to its default state, and (installFocusZoomGuard)
// prevents the iOS input-focus auto-zoom without locking zoom for everyone.
//
// Why this exists: index.html pins the document with `body{position:fixed;
// overflow:hidden}` and scrolls inside #root instead. When iOS pans or scales the
// VISUAL viewport to reveal a focused input, the document itself never scrolled,
// so `window.scrollTo(0,0)` has nothing to undo and the app stays zoomed/shifted
// after the keyboard closes. Resetting means blurring the field, unscrolling the
// real scroll containers, and forcing WebKit to re-apply scale 1.
//
// Must match public/index.html's viewport meta. Pinch zoom is deliberately
// allowed (accessibility pass 2026-09-12) — no user-scalable=no, no permanent
// maximum-scale.
//
// THE 16PX PREMISE IS NOT SUFFICIENT ON DEVICE (sign-in zoom regression,
// 2026-09-14): every text input computes to exactly 16px (runtime-verified
// against the production build), yet iOS 26's WKWebView still focus-zoomed the
// sign-in screen — the same stray focus-zoom Scorify.jsx's kbInset scale guard
// already recorded. So 16px is kept as a necessary floor, but the guard below
// no longer trusts it to be sufficient: while an editable element is focused,
// the viewport is clamped to maximum-scale=1 so the OS zoom is a no-op, and
// the clamp is released as soon as focus leaves. Pinch zoom is therefore
// unavailable only WHILE TYPING; everywhere else it stays free. Side effect,
// accepted (and pre-existing in resetViewport): a deliberate pinch-zoom is
// snapped back to 1 when a field takes focus or a keyboard dismissal resets.
const VIEWPORT_CONTENT =
  "width=device-width, initial-scale=1.0, viewport-fit=cover";
const VIEWPORT_CLAMPED = VIEWPORT_CONTENT + ", maximum-scale=1.0";

const isEditable = (el) =>
  !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);

const setMeta = (content) => {
  const meta = document.querySelector('meta[name="viewport"]');
  if (meta && meta.getAttribute("content") !== content) meta.setAttribute("content", content);
};

// Release-to-state, never unconditional: if focus has already moved to another
// editable field by the time a release timer fires, the clamp must stay.
const releaseMeta = () => {
  if (!isEditable(document.activeElement)) setMeta(VIEWPORT_CONTENT);
};

export default function resetViewport() {
  if (typeof document === "undefined") return;

  // 1. Blur first. While a field holds focus, WebKit keeps the visual viewport
  //    parked over it and will re-apply any pan we undo below.
  const el = document.activeElement;
  if (el && isEditable(el)) {
    try { el.blur(); } catch {}
  }

  // 2. Unscroll every real scroll container, horizontally included — a horizontal
  //    offset is what reads as "shifted right", and nothing else clears it.
  try { window.scrollTo(0, 0); } catch {}
  const root = document.getElementById("root");
  if (root) { root.scrollLeft = 0; }
  if (document.scrollingElement) {
    document.scrollingElement.scrollLeft = 0;
    document.scrollingElement.scrollTop = 0;
  }

  // 3. Force WebKit to re-apply scale 1: clamp, hold, release. This used to
  //    release on the next requestAnimationFrame, but WebKit applies a viewport
  //    meta change in its own task — a single rAF can fire before the clamp has
  //    landed, releasing it before it ever snapped the scale (the reset "ran"
  //    and nothing changed). Hold the clamp briefly instead; the release keeps
  //    pinch zoom available and defers to the focus guard if a field has
  //    focus again by then.
  setMeta(VIEWPORT_CLAMPED);
  setTimeout(releaseMeta, 150);
}

// ── FOCUS-ZOOM GUARD ─────────────────────────────────────────────────────────
// Install once at startup (src/index.js). Clamps the viewport BEFORE the OS
// decides to zoom: touchstart fires ahead of focus, focusin covers keyboard
// and programmatic focus. focusout releases after a short delay so focus
// moving directly between two fields never flickers the clamp off.
let guardInstalled = false;
export function installFocusZoomGuard() {
  if (guardInstalled || typeof document === "undefined") return;
  guardInstalled = true;
  document.addEventListener("touchstart", (e) => {
    if (isEditable(e.target)) setMeta(VIEWPORT_CLAMPED);
  }, { capture: true, passive: true });
  document.addEventListener("focusin", (e) => {
    if (isEditable(e.target)) setMeta(VIEWPORT_CLAMPED);
  });
  document.addEventListener("focusout", () => {
    setTimeout(releaseMeta, 80);
  });
  // PROGRAMMATIC focus has no touchstart to pre-clamp it, and focusin fires
  // only after the browser has already decided whether to zoom — WebKit
  // applies a viewport-meta change in its own task, so the focusin clamp
  // sometimes loses the race. That race was visible as the intermittent
  // sign-in glitch: React's autoFocus ran during the screen's entrance, the
  // OS zoomed toward the field, then the late clamp snapped scale back.
  // Wrapping focus() clamps synchronously BEFORE the focus call, the same
  // ordering touchstart gives taps, closing the race for every programmatic
  // focus site (autoFocus included) now and in the future.
  try {
    const origFocus = HTMLElement.prototype.focus;
    HTMLElement.prototype.focus = function focusWithZoomClamp(...args) {
      try { if (isEditable(this)) setMeta(VIEWPORT_CLAMPED); } catch {}
      return origFocus.apply(this, args);
    };
  } catch { /* guard must never break focus itself */ }
}
